import { useEffect, useState } from 'react';
import { ContactsPage } from './ContactsPage';
import { CampaignsPage } from './CampaignsPage';
import { CertificationPage } from './CertificationPage';

type HubStep = 'destinatarios' | 'acoes' | 'certificacao';

const stepLabels: Record<HubStep, string> = {
  destinatarios: 'Preparação',
  acoes: 'Execução',
  certificacao: 'Fechamento',
};

const stepTitles: Record<HubStep, string> = {
  destinatarios: 'Destinatários',
  acoes: 'Ações de envio',
  certificacao: 'Certificação',
};

const stepDescriptions: Record<HubStep, string> = {
  destinatarios: 'Cadastre, importe e organize a base antes de seguir para o envio.',
  acoes: 'Crie as listas e acompanhe os disparos sem sair do fluxo principal.',
  certificacao: 'Feche o lote com clareza, aproveitando o que já foi produzido nas etapas anteriores.',
};

export function MassCompliancePage({ initialStep }: { initialStep?: HubStep }) {
  const [selectedStep, setSelectedStep] = useState<HubStep | null>(initialStep ?? null);

  useEffect(() => {
    setSelectedStep(initialStep ?? null);
  }, [initialStep]);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <section
        className="rounded-[28px] border border-slate-200 p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.2)] sm:p-8"
        style={{
          background:
            'radial-gradient(circle at top left, rgba(125, 211, 252, 0.24), transparent 28%), linear-gradient(135deg, #0f172a 0%, #1d4ed8 48%, #38bdf8 100%)',
        }}
      >
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200/90">
            Fluxo unificado
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            Central de Cumprimento
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
            Escolha uma etapa para trabalhar. A área de trabalho só aparece quando você entrar em uma das três fases do fluxo.
          </p>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-3">
        {(['destinatarios', 'acoes', 'certificacao'] as HubStep[]).map((step) => {
          const isActive = selectedStep === step;

          return (
            <button
              key={step}
              type="button"
              onClick={() => setSelectedStep(step)}
              className={`rounded-[24px] border p-5 text-left transition-all ${
                isActive
                  ? 'border-slate-900 bg-slate-900 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]'
                  : 'border-slate-200 bg-white text-slate-900 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${isActive ? 'text-cyan-200' : 'text-slate-500'}`}>
                {stepLabels[step]}
              </p>
              <h2 className="mt-3 text-xl font-semibold">{stepTitles[step]}</h2>
              <p className={`mt-2 text-sm leading-6 ${isActive ? 'text-slate-200' : 'text-slate-600'}`}>
                {stepDescriptions[step]}
              </p>
              <span className={`mt-5 inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ${
                isActive ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'
              }`}>
                Abrir área
              </span>
            </button>
          );
        })}
      </section>

      {selectedStep && (
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div
            className="border-b border-slate-100 px-5 py-5 text-white sm:px-6"
            style={{
              background:
                'radial-gradient(circle at top left, rgba(56, 189, 248, 0.22), transparent 28%), linear-gradient(135deg, #0f172a 0%, #1e3a8a 52%, #0f766e 100%)',
            }}
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200/90">
                  {stepLabels[selectedStep]}
                </p>
                <h2 className="mt-1 text-2xl font-semibold">
                  {stepTitles[selectedStep]}
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-200">
                  {stepDescriptions[selectedStep]}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedStep(null)}
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                Retornar
              </button>
            </div>

            <div className="mt-3 grid gap-2 lg:grid-cols-3">
              {(['destinatarios', 'acoes', 'certificacao'] as HubStep[]).map((step, index) => {
                const isCurrent = selectedStep === step;
                const isPast =
                  (selectedStep === 'acoes' && step === 'destinatarios') ||
                  (selectedStep === 'certificacao' && (step === 'destinatarios' || step === 'acoes'));

                return (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setSelectedStep(step)}
                    className={`rounded-xl border px-3 py-1.5 text-left transition ${
                      isCurrent
                        ? 'border-cyan-200/70 bg-white/18 text-white'
                        : isPast
                          ? 'border-emerald-200/70 bg-emerald-50/18 text-emerald-50'
                          : 'border-sky-100/35 bg-slate-900/22 text-slate-100 hover:bg-slate-900/30'
                    }`}
                  >
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${
                      isPast ? 'text-emerald-100' : 'text-cyan-100'
                    }`}>
                      Etapa {index + 1}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{stepTitles[step]}</p>
                      <span
                        className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                          isCurrent
                            ? 'bg-white text-slate-900'
                          : isPast
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-slate-100/20 text-white'
                        }`}
                      >
                        {isPast ? 'OK' : index + 1}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-slate-50/60 p-4 sm:p-5">
            {selectedStep === 'destinatarios' && <ContactsPage embedded={true} />}
            {selectedStep === 'acoes' && <CampaignsPage embedded={true} />}
            {selectedStep === 'certificacao' && <CertificationPage embedded={true} />}
          </div>
        </section>
      )}
    </div>
  );
}
